import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BookOpen, X, CheckCircle2, Clock } from 'lucide-react';
import { SeriesTracking, useSeriesTracking } from '@/hooks/useSeriesTracking';
import { CoverImage } from '@/components/CoverImage';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Link } from 'react-router-dom';
import { FindNextBookButton } from '@/components/FindNextBookButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface SeriesTrackerCardProps {
  series: SeriesTracking;
  showDismissButton?: boolean;
  showReactivateButton?: boolean;
}

export function SeriesTrackerCard({
  series,
  showDismissButton = true,
  showReactivateButton = false,
}: SeriesTrackerCardProps) {
  const { dismissSeries, reactivateSeries, useSeriesBooks } = useSeriesTracking();
  const { data: seriesBooks = [] } = useSeriesBooks(series.series_name, series.author_name);

  const progressPercent = series.total_books_known
    ? Math.round((series.highest_book_position / series.total_books_known) * 100)
    : 0;

  const isComplete = series.total_books_known
    ? series.highest_book_position >= series.total_books_known
    : false;

  return (
    <Card className="card-elevated">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl font-bold mb-1 line-clamp-2">
              {series.series_name_display}
            </CardTitle>
            <p className="text-sm text-muted-foreground">by {series.author_name}</p>
          </div>
          {showReactivateButton ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => reactivateSeries(series.id)}
            >
              Reactivate
            </Button>
          ) : showDismissButton ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Dismiss this series?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove it from your active series tracking. You can reactivate it
                    later from the Dismissed tab.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => dismissSeries({ seriesId: series.id })}>
                    Dismiss
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <div className="flex items-center gap-2">
              {isComplete ? (
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Complete
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <Clock className="h-3 w-3" />
                  In Progress
                </Badge>
              )}
              <span className="font-medium">
                Book {series.highest_book_position}
                {series.total_books_known ? ` of ${series.total_books_known}` : ''}
              </span>
            </div>
          </div>
          {series.total_books_known && (
            <Progress value={progressPercent} className="h-2" />
          )}
        </div>

        {/* Series Books */}
        {seriesBooks.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Your Books in This Series
            </h4>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-3 pb-4">
                {seriesBooks
                  .sort((a, b) => {
                    const posA = (a.books as any)?.series_position || 0;
                    const posB = (b.books as any)?.series_position || 0;
                    return posA - posB;
                  })
                  .map((userBook) => {
                    const book = userBook.books as any;
                    const isRead = userBook.shelf === 'read';
                    return (
                      <Link
                        key={userBook.id}
                        to={`/book/${book.id}`}
                        className="relative group shrink-0"
                      >
                        <div className="relative">
                          <CoverImage
                            bookId={book.id}
                            coverUrl={book.cover_url}
                            title={book.title}
                            size="sm"
                            className={!isRead ? 'opacity-50' : ''}
                          />
                          {book.series_position && (
                            <Badge
                              variant={isRead ? 'default' : 'secondary'}
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs"
                            >
                              {book.series_position}
                            </Badge>
                          )}
                        </div>
                        {isRead && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                            <CheckCircle2 className="h-8 w-8 text-white" />
                          </div>
                        )}
                      </Link>
                    );
                  })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {/* Find Next Book Button */}
        {!series.is_dismissed && <FindNextBookButton series={series} />}

        {/* Dismiss Reason (if dismissed) */}
        {series.is_dismissed && series.dismiss_reason && (
          <div className="text-sm text-muted-foreground italic">
            Reason: {series.dismiss_reason}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
